"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import MaterialCta from "./MaterialCta";

const DEFAULT_IMAGE_BASE_PATH = "/image/new-version/molding/injection-molding/materials";

// 材料数据结构
export interface MaterialItem {
	key: string;
	images: string[];
}

// 默认金属材料数据 - 每种材料有5张图片
const DEFAULT_METAL_MATERIALS: MaterialItem[] = [
	{
		key: "Aluminum",
		images: ["abs-1.webp", "abs-2.webp", "abs-3.webp", "abs-4.webp", "abs-5.webp"],
	},
	{
		key: "Bronze",
		images: ["pc-1.webp", "pc-2.webp", "pc-3.webp", "pc-4.webp", "pc-5.webp"],
	},
	{
		key: "StainlessSteel",
		images: [
			"pmma-acrylic-1.jpg",
			"pmma-acrylic-2.jpg",
			"pmma-acrylic-3.jpg",
			"pmma-acrylic-4.jpg",
			"pmma-acrylic-5.jpg",
		],
	},
	{
		key: "CarbonSteel",
		images: ["pom-1.webp", "pom-2.webp", "pom-3.webp", "pom-4.webp", "pom-5.webp"],
	},
	{
		key: "Titanium",
		images: ["nylon-1.webp", "nylon-2.webp", "nylon-3.webp", "nylon-4.webp", "nylon-5.webp"],
	},
	{
		key: "Brass",
		images: ["brass-01.webp", "brass-02.webp", "brass-03.webp", "brass-04.webp", "brass-05.jpg"],
	},
	{
		key: "Copper",
		images: ["copper-01.jpeg", "copper-02.jpeg", "copper-03.jpeg", "copper-04.jpeg", "copper-05.jpeg"],
	},
];

// 默认塑料材料数据
const DEFAULT_PLASTIC_MATERIALS: MaterialItem[] = [
	{
		key: "ABS",
		images: ["abs-01.jpeg", "abs-02.jpeg", "abs-03.jpeg", "abs-04.jpeg", "abs-05.jpeg"],
	},
	{
		key: "PC",
		images: ["pc-01.jpeg", "pc-02.jpeg", "pc-03.jpeg", "pc-04.jpeg", "pc-05.jpeg"],
	},
	{
		key: "PMMA",
		images: ["pmma-01.jpeg", "pmma-02.jpeg", "pmma-03.jpeg", "pmma-04.jpeg", "pmma-05.jpeg"],
	},
	{
		key: "POM",
		images: ["pom-01.jpeg", "pom-02.jpeg", "pom-03.jpeg", "pom-04.jpeg"],
	},
	{
		key: "Nylon",
		images: [
			"pa-nylon-01.jpeg",
			"pa-nylon-02.jpeg",
			"pa-nylon-03.jpeg",
			"pa-nylon-04.jpeg",
			"pa-nylon-05.jpeg",
		],
	},
	{
		key: "PE",
		images: ["pe-01.jpeg", "pe-02.jpeg", "pe-03.jpeg", "pe-04.jpeg", "pe-05.jpeg"],
	},
	{
		key: "PEEK",
		images: ["peek-01.jpeg", "peek-02.jpeg", "peek-03.jpeg", "peek-04.jpeg", "peek-05.jpeg"],
	},
	{
		key: "PP",
		images: ["pp-01.jpeg", "pp-02.jpeg", "pp-03.jpeg", "pp-04.jpeg"],
	},
	{
		key: "HDPE",
		images: ["hdpe-01.jpeg", "hdpe-02.jpeg", "hdpe-03.jpeg", "hdpe-04.jpeg", "hdpe-05.jpeg"],
	},
	{
		key: "HIPS",
		images: ["hips-01.jpeg", "hips-02.jpeg", "hips-03.jpeg", "hips-04.jpeg", "hips-05.jpeg"],
	},
	{
		key: "LDPE",
		images: ["ldpe-01.jpeg", "ldpe-02.jpeg", "ldpe-03.jpeg"],
	},
	{
		key: "PBT",
		images: ["pbt-01.jpeg", "pbt-02.jpeg", "pbt-03.jpeg", "pbt-04.jpeg", "pbt-05.jpeg"],
	},
	{
		key: "PPA",
		images: ["ppa-01.jpeg", "ppa-02.jpeg", "ppa-03.jpeg", "ppa-04.jpeg", "ppa-05.jpeg"],
	},
	{
		key: "PAI",
		images: ["pai-01.jpeg", "pai-02.jpeg", "pai-03.jpeg", "pai-04.jpeg", "pai-05.jpeg"],
	},
	{
		key: "PET",
		images: ["pet-01.jpeg", "pet-02.jpeg", "pet-03.jpeg", "pet-04.jpeg"],
	},
	{
		key: "PPS",
		images: ["pps-01.jpeg", "pps-02.jpeg", "pps-03.jpeg"],
	},
	{
		key: "PS",
		images: ["ps-01.jpeg", "ps-02.jpeg", "ps-03.jpeg", "ps-04.jpeg"],
	},
	{
		key: "PVC",
		images: ["pvc-01.jpeg", "pvc-02.jpeg", "pvc-03.jpeg", "pvc-04.jpeg", "pvc-05.jpeg"],
	},
	{
		key: "PTFE",
		images: ["ptfe-01.jpeg", "ptfe-02.jpeg", "ptfe-03.jpeg", "ptfe-04.jpeg", "ptfe-05.jpeg"],
	},
	{
		key: "UPE",
		images: ["upe-01.jpeg", "upe-02.jpeg", "upe-03.jpeg", "upe-04.jpeg"],
	},
];

// Gear Machining 金属材料
export const GEAR_METAL_MATERIALS: MaterialItem[] = [
	{
		key: "Aluminum",
		images: ["aluminum-01.png", "aluminum-02.png", "aluminum-03.png", "aluminum-04.png"],
	},
	{
		key: "Copper",
		images: ["copper-01.png", "copper-02.png", "copper-03.png", "copper-04.png"],
	},
	{
		key: "Bronze",
		images: ["bronze-01.png", "bronze-02.png", "bronze-03.png", "bronze-04.png"],
	},
	{
		key: "CarbonSteel",
		images: ["carbon-steel-01.png", "carbon-steel-02.png", "carbon-steel-03.png", "carbon-steel-04.png"],
	},
	{
		key: "StainlessSteel",
		images: [
			"stainless-steel-01.png",
			"stainless-steel-02.png",
			"stainless-steel-03.png",
			"stainless-steel-04.png",
		],
	},
	{
		key: "Titanium",
		images: ["titanium-01.png", "titanium-02.png", "titanium-03.png", "titanium-04.png"],
	},
	{
		key: "Brass",
		images: ["brass-01.png", "brass-02.png", "brass-03.png", "brass-04.png"],
	},
];

// Gear Machining 塑料材料
export const GEAR_PLASTIC_MATERIALS: MaterialItem[] = [
	{
		key: "POM",
		images: ["pom-01.png", "pom-02.png", "pom-03.png", "pom-04.png"],
	},
	{
		key: "Nylon",
		images: ["nylon-pa-01.png", "nylon-pa-02.png", "nylon-pa-03.png", "nylon-pa-04.png"],
	},
	{
		key: "PC",
		images: ["pc-01.png", "pc-02.png", "pc-03.png", "pc-04.png"],
	},
	{
		key: "PEEK",
		images: ["peek-01.png", "peek-02.png", "peek-03.png", "peek-04.png"],
	},
	{
		key: "PPS",
		images: ["pps-01.png", "pps-02.png", "pps-03.png", "pps-04.png"],
	},
	{
		key: "LCP",
		images: ["lcp-01.png", "lcp-02.png", "lcp-03.png", "lcp-04.png"],
	},
	{
		key: "PBTPET",
		images: ["pbt-pet-01.png", "pbt-pet-02.png", "pbt-pet-03.png", "pbt-pet-04.png"],
	},
];

/**
 * CNC 加工页专用：静态资源目录 `/image/new-version/machining/cnc-machining/materials`
 * 金属图命名与齿轮加工 `gear/cailiao` 一致（*.png），顺序与 cncMachiningMaterials 文案键一致。
 * 塑料仍使用组件内 DEFAULT_PLASTIC_MATERIALS 的文件名（如 abs-01.jpeg），需放在同一 cailiao 目录下。
 */
export const CNC_MACHINING_CAILIAO_METAL_MATERIALS: MaterialItem[] = [
	{
		key: "Aluminum",
		images: [
			"aluninum-01.jpeg",
			"aluninum-02.jpeg",
			"aluninum-03.jpeg",
			"aluninum-04.jpeg",
			"aluninum-05.jpeg",
		],
	},
	{
		key: "Bronze",
		images: ["bronze-01.jpeg", "bronze-02.jpeg", "bronze-03.jpeg", "bronze-04.jpeg", "bronze-05.jpeg"],
	},
	{
		key: "StainlessSteel",
		images: [
			"stainless-steel-01.jpeg",
			"stainless-steel-02.jpeg",
			"stainless-steel-03.jpeg",
			"stainless-steel-04.jpeg",
			"stainless-steel-05.jpeg",
		],
	},
	{
		key: "CarbonSteel",
		images: [
			"carbon-steel-01.jpeg",
			"carbon-steel-02.jpeg",
			"carbon-steel-03.jpeg",
			"carbon-steel-04.jpeg",
			"carbon-steel-05.jpeg",
		],
	},
	{
		key: "Titanium",
		images: [
			"titanium-01.jpeg",
			"titanium-02.jpeg",
			"titanium-03.jpeg",
			"titanium-04.jpeg",
			"titanium-05.jpeg",
		],
	},
	{
		key: "Brass",
		images: ["brass-01.webp", "brass-02.webp", "brass-03.webp", "brass-04.webp"],
	},
	{
		key: "Copper",
		images: ["copper-01.jpeg", "copper-02.jpeg", "copper-03.jpeg", "copper-04.jpeg", "copper-05.jpeg"],
	},
];

/** 钣金加工页：`Sheet Metal Materials for Custom Fabrication` 目录下按材料文件名 */
export const SHEET_METAL_FABRICATION_MATERIALS: MaterialItem[] = [
	{
		key: "Aluminium",
		images: ["aluminium-1.webp", "aluminium-2.webp", "aluminium-3.webp", "aluminium-4.webp"],
	},
	{
		key: "StainlessSteel",
		images: [
			"stainless-steel-1.webp",
			"stainless-steel-2.webp",
			"stainless-steel-3.webp",
			"stainless-steel-4.webp",
		],
	},
	{
		key: "Copper",
		images: ["copper-1.webp", "copper-2.webp", "copper-3.webp", "copper-4.webp"],
	},
	{
		key: "Brass",
		images: ["brass-1.webp", "brass-2.webp", "brass-3.webp", "brass-4.webp"],
	},
	{
		key: "Steel",
		images: ["steel-1.webp", "steel-2.webp", "steel-3.webp", "steel-4.webp"],
	},
];

export interface CncMachiningMaterialsProps {
	translationNamespace?: string;
	imageBasePath?: string;
	metalMaterials?: MaterialItem[];
	plasticMaterials?: MaterialItem[];
	/** CTA 文案最大宽度 */
	ctaTextMaxWidth?: string;
	/** CTA 按钮文字最大宽度（可选，默认由 MaterialCta 内部处理） */
	ctaButtonTextMaxWidth?: string;
	/** 仅展示塑料（用于注塑页）：不展示金属分类，左侧只显示 PLASTICS */
	plasticOnly?: boolean;
	/** 仅展示金属（用于钣金页）：不展示塑料分类与列表 */
	metalOnly?: boolean;
	/** 是否展示「表面处理」行，注塑页可设为 false */
	showFinishing?: boolean;
	/** CTA 按钮跳转链接，可传外链如 /upload */
	ctaButtonHref?: string;
	/** 底部 CTA 左侧文案，传入则优先于命名空间中的 ctaText */
	ctaText?: string;
	/** 底部 CTA 按钮文字，传入则优先于命名空间中的 ctaButton */
	ctaButtonText?: string;
	/** 为 true 时不渲染底部 MaterialCta（嵌件注塑等页可关闭） */
	hideCta?: boolean;
}

const CncMachiningMaterials = ({
	translationNamespace = "cncMachiningMaterials",
	imageBasePath = DEFAULT_IMAGE_BASE_PATH,
	metalMaterials = DEFAULT_METAL_MATERIALS,
	plasticMaterials = DEFAULT_PLASTIC_MATERIALS,
	ctaTextMaxWidth,
	ctaButtonTextMaxWidth,
	plasticOnly = false,
	metalOnly = false,
	showFinishing = true,
	ctaButtonHref,
	ctaText,
	ctaButtonText,
	hideCta = false,
}: CncMachiningMaterialsProps) => {
	const t = useTranslations(translationNamespace);
	const [activeIndex, setActiveIndex] = useState(0);
	const [activeImageIndex, setActiveImageIndex] = useState(0);

	// 仅塑料 / 仅金属 / 金属+塑料
	const allMaterials = plasticOnly
		? plasticMaterials.map((m) => ({ ...m, type: "plastic" as const }))
		: metalOnly
			? metalMaterials.map((m) => ({ ...m, type: "metal" as const }))
			: [
					...metalMaterials.map((m) => ({ ...m, type: "metal" as const })),
					...plasticMaterials.map((m) => ({ ...m, type: "plastic" as const })),
				];

	const activeMaterial = allMaterials[activeIndex];
	const prefix = activeMaterial.type === "metal" ? "metal" : "plastic";

	// 获取当前材料的详细信息
	const materialName = t(`${prefix}${activeMaterial.key}` as any);
	const materialDesc = t(`${prefix}${activeMaterial.key}Desc` as any);
	const materialColor = t(`${prefix}${activeMaterial.key}Color` as any);
	const materialGrades = t(`${prefix}${activeMaterial.key}Grades` as any);
	const materialFinishing = t(`${prefix}${activeMaterial.key}Finishing` as any);
	const materialPrice = t(`${prefix}${activeMaterial.key}Price` as any);
	const materialDelivery = t(`${prefix}${activeMaterial.key}Delivery` as any);

	// 切换材料时重置图片索引
	const handleMaterialChange = (index: number) => {
		setActiveIndex(index);
		setActiveImageIndex(0);
	};

	return (
		<section className="w-full bg-[#f6f6f6] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题区域 */}
				<div className="mx-auto mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("subtitle")}</p>
				</div>

				{/* 主内容区域 */}
				<div className="mx-auto flex flex-col gap-0 bg-white lg:flex-row">
					{/* 左侧材料列表 */}
					<div className="w-full shrink-0 bg-[#f1f1f1] lg:w-[320px]">
						{!plasticOnly && (
							/* 金属材料分类（与塑料并列时底部分隔；仅金属页无下边框） */
							<div className={metalOnly ? "" : "border-b border-gray-200"}>
								<h3 className="mb-2 ml-2 mt-4 w-fit rounded-full bg-white px-2 py-1 text-sm font-bold uppercase tracking-wider text-black">
									{t("metalsTitle")}
								</h3>
								<div className="flex flex-wrap gap-1 pb-2 lg:flex-col lg:gap-0">
									{metalMaterials.map((material: MaterialItem, index: number) => (
										<button
											key={material.key}
											onClick={() => handleMaterialChange(index)}
											className={`w-full px-5 py-3 text-left text-sm font-medium transition-colors ${
												activeIndex === index
													? "bg-white text-themePrimary400"
													: "text-gray-600 hover:bg-white hover:text-themePrimary400"
											}`}
										>
											{t(`metal${material.key}` as any)}
										</button>
									))}
								</div>
							</div>
						)}

						{/* 塑料材料分类（metalOnly 时隐藏，如钣金页） */}
						{!metalOnly && (
							<div>
								<h3 className="mb-2 ml-2 mt-4 w-fit rounded-full bg-white px-2 py-1 text-sm font-bold uppercase tracking-wider text-black">
									{t("plasticsTitle")}
								</h3>
								<div className="max-h-[400px] overflow-y-auto">
									<div className="flex flex-wrap gap-1 pb-4 lg:flex-col lg:gap-0">
										{plasticMaterials.map((material: MaterialItem, index: number) => {
											const globalIndex = plasticOnly ? index : metalMaterials.length + index;
											return (
												<button
													key={material.key}
													onClick={() => handleMaterialChange(globalIndex)}
													className={`w-full px-5 py-2.5 text-left text-sm font-medium transition-colors ${
														activeIndex === globalIndex
															? "bg-white text-themePrimary400"
															: "text-gray-600 hover:bg-white hover:text-themePrimary400"
													}`}
												>
													{t(`plastic${material.key}` as any)}
												</button>
											);
										})}
									</div>
								</div>
							</div>
						)}
					</div>

					{/* 右侧内容区域 */}
					<div className="flex-1 p-6 lg:p-8">
						{/* 材料名称标签 */}
						<div className="mb-6">
							<span className="inline-block rounded bg-themePrimary400 px-4 py-3 text-sm font-semibold text-white">
								{materialName}
							</span>
						</div>

						<div className="flex flex-col gap-8 border-t border-black max-md:gap-4 lg:flex-row lg:gap-0">
							{/* 左侧图片区域 */}
							<div className="w-full border-b border-black p-8 pb-8 max-md:p-4 lg:w-1/2 lg:border-b-0 lg:border-r">
								{/* 主图 */}
								<div className="relative mb-4 aspect-[4/2] w-full overflow-hidden bg-[#bfbfbf]">
									<Image
										key={`${activeMaterial.key}-${activeImageIndex}`}
										src={`${imageBasePath}/${activeMaterial.images[activeImageIndex]}`}
										alt={materialName}
										fill
										priority
										className="object-contain p-4"
										sizes="(max-width: 768px) 100vw, 400px"
									/>
								</div>

								{/* 缩略图列表 - 一行3个均匀分布 */}
								<div className="grid grid-cols-3 gap-3">
									{activeMaterial.images.map((image: string, index: number) => (
										<button
											key={`${activeMaterial.key}-thumb-${index}`}
											onClick={() => setActiveImageIndex(index)}
											className={`relative aspect-[4/3] w-full overflow-hidden border-2 transition-all ${
												activeImageIndex === index
													? "border-themePrimary400"
													: "border-gray-200 hover:border-gray-400"
											}`}
										>
											<Image
												src={`${imageBasePath}/${image}`}
												alt={`${materialName} ${index + 1}`}
												fill
												priority={index === 0}
												className="object-contain p-2"
												sizes="(max-width: 768px) 30vw, 120px"
											/>
										</button>
									))}
								</div>
							</div>

							{/* 右侧详情区域 */}
							<div className="w-full p-8 max-md:p-4 lg:w-1/2">
								{/* 材料名称 */}
								<h3 className="mb-4 text-lg font-bold text-black sm:text-xl md:text-2xl">{materialName}</h3>

								{/* 材料描述 */}
								<p className="mb-6 text-sm leading-relaxed text-gray-900">{materialDesc}</p>

								{/* 详细属性 */}
								<div className="space-y-4">
									{/* 颜色 */}
									<div className="flex items-start gap-4">
										<span className="w-32 shrink-0 text-sm font-semibold text-black">{t("labelColor")}:</span>
										<span className="inline-flex items-center gap-2 rounded-full bg-gray-100 py-1.5 pl-2 pr-4 text-sm text-gray-900">
											<span className="inline-block h-5 w-5 rounded-full border border-gray-200 bg-gray-300"></span>
											{materialColor}
										</span>
									</div>

									{/* 常见型号 / Subtypes */}
									<div className="flex items-start gap-4">
										<span className="w-32 shrink-0 text-sm font-semibold text-black">
											{t("labelGrades")}:
										</span>
										<span className="text-sm text-gray-900">{materialGrades}</span>
									</div>

									{/* 表面处理（可隐藏，注塑页用 Subtypes 不需此项） */}
									{showFinishing && (
										<div className="flex items-start gap-4">
											<span className="w-32 shrink-0 text-sm font-semibold text-black">
												{t("labelFinishing")}:
											</span>
											<span className="text-sm text-gray-900">{materialFinishing}</span>
										</div>
									)}

									{/* 价格 */}
									<div className="flex items-start gap-4">
										<span className="w-32 shrink-0 text-sm font-semibold text-black">{t("labelPrice")}:</span>
										<span className="text-sm font-medium text-gray-900">{materialPrice}</span>
									</div>

									{/* 交货时间 */}
									<div className="flex items-start gap-4">
										<span className="w-32 shrink-0 text-sm font-semibold text-black">
											{t("labelDelivery")}:
										</span>
										<span className="text-sm text-gray-900">{materialDelivery}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* 底部CTA */}
				{!hideCta ? (
					<MaterialCta
						translationNamespace={translationNamespace}
						text={ctaText}
						buttonText={ctaButtonText}
						buttonHref={ctaButtonHref}
						textMaxWidth={ctaTextMaxWidth}
						buttonTextMaxWidth={ctaButtonTextMaxWidth}
					/>
				) : null}
			</div>
		</section>
	);
};

export default CncMachiningMaterials;
